<template>
  <div>

    <modal-component :size="modalSize" v-model="modal.active">
      <div slot="title">
        {{ modal.title }}
        <a :href="'/' + model + '/edit/' + editID" target="_blank" class="text-orange" v-if="editID">
          #{{ editID }}
          <i class="fe-external-link"></i>
        </a>
      </div>
      <template v-if="model === 'customer' || model === 'supplier'">
        <create-contact
          ref="contact"
          :manual-contact-type="model"
          :contactID="editID"
          :create-alert="false"
          :model-options="modelOptions"
          @created="closeModal('contact', $event)"
          @updated="closeModal('contact', $event)"
        ></create-contact>
      </template>
      <template v-else-if="model === 'vehicle'">
        <create-vehicle
          ref="vehicle"
          :in-modal="true"
          :vehicleID="editID"
          :create-alert="false"
          :model-options="modelOptions"
          @created="closeModal('vehicle', $event)"
          @updated="closeModal('vehicle', $event)"
        ></create-vehicle>
      </template>
      <template v-else-if="model === 'job-type'">
        <create-job-type
          ref="jobType"
          :in-modal="true"
          :jobTypeID="editID"
          :create-alert="false"
          :model-options="modelOptions"
          @created="closeModal('job-type', $event)"
          @updated="closeModal('job-type', $event)"
        ></create-job-type>
      </template>
      <template v-else-if="model === 'product'">
        <create-product
          ref="product"
          :productID="editID"
          :in-modal="true"
          :create-alert="false"
          :model-options="modelOptions"
          @created="closeModal('product', $event)"
          @updated="closeModal('product', $event)"
        ></create-product>
      </template>
      <template v-else-if="model === 'reminder-list'">
        <create-reminder-list
          ref="reminderList"
          :listID="editID"
          :in-modal="true"
          :create-alert="false"
          :model-options="modelOptions"
          @created="closeModal('reminder-list', $event)"
          @updated="closeModal('reminder-list', $event)"
        ></create-reminder-list>
      </template>
      <template v-else-if="model === 'supplier-account'">
        <create-supplier-account
          ref="supplierAccount"
          :listID="editID"
          :in-modal="true"
          :create-alert="false"
          :model-options="modelOptions"
          @created="closeModal('supplier-account', $event)"
          @updated="closeModal('supplier-account', $event)"
        ></create-supplier-account>
      </template>
    </modal-component>

    <template v-if="noWrap">
      <multiselect
        @open="open"
        @close="close"
        ref="multiselect"
        :key="model + '-' + _uid"
        :name="formName"
        :value.sync="value"
        @input="$emit('input', $event)"
        :options="list"
        :label="label"
        :track-by="trackBy"
        :placeholder="placeholder"
        @search-change="search"
        @select="$emit('select', $event)"
        @remove="$emit('remove', $event)"
        :taggable="isTaggable"
        :tag-placeholder="tagPlaceholder"
        @tag="create"
        :selectLabel="''"
        :selectedLabel="''"
        :deselectLabel="''"
        :loading="isLoading"
      ></multiselect>

      <ValidationProvider
        :rules="validationRules"
        :name="dataVvAs || formName || model"
        v-slot="{ errors }"
      >
        <input type="hidden" v-model="value">
        <span class="text-sm text-red-500">{{ errors[0] }}</span>
      </ValidationProvider>
    </template>

    <template v-else>
      <div class="flex flex-wrap">
        <div class="w-full">
          <div>
            <label class="block mb-1 font-bold" v-if="showTitle">
              {{ dataVvAs || formName || model | sentenceCase }}
              <span class="text-red-500" v-if="validationRules.indexOf('required') !== -1">*</span>
            </label>
            <multiselect
              @open="open"
              @close="close"
              ref="multiselect"
              :key="model + '-' + _uid"
              :name="formName"
              :value.sync="value"
              @input="$emit('input', $event)"
              :options="list"
              :label="label"
              :track-by="trackBy"
              :placeholder="placeholder"
              @search-change="search"
              @select="$emit('select', $event)"
              @remove="$emit('remove', $event)"
              :taggable="isTaggable"
              :tag-placeholder="tagPlaceholder"
              @tag="create"
              :selectLabel="''"
              :selectedLabel="''"
              :deselectLabel="''"
              :loading="isLoading"
            ></multiselect>

            <ValidationProvider
              :rules="validationRules"
              :name="dataVvAs || formName || model"
              v-slot="{ errors }"
            >
              <input type="hidden" v-model="value">
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap mt-2" v-if="!inModal && (buttonNew || buttonEdit)">
        <button
          v-if="buttonNew"
          type="button"
          class="btn btn-purple mr-1"
          @click="create('')"
        >New</button>
        <button
          v-if="value && value.id"
          type="button"
          class="btn btn-green"
          @click="edit"
        >Edit</button>
      </div>
    </template>

  </div>
</template>

<script>
import axios from 'axios'
import path from 'path'
const ModalComponent = resolve => require(['@/components/Modal'], resolve)
const CreateContact = resolve => require(['@/components/pages/contacts/Edit'], resolve)
const CreateVehicle = resolve => require(['@/components/pages/vehicles/Edit'], resolve)
const CreateJobType = resolve => require(['@/components/pages/jobs/JobTypeEdit'], resolve)
const CreateProduct = resolve => require(['@/components/pages/products/Edit'], resolve)
const CreateSupplierAccount = resolve => require(['@/components/pages/suppliers/AccountEdit.vue'], resolve)
const CreateReminderList = resolve => require(['@/components/pages/reminders/list/Edit'], resolve)

export default {
  name: 'model-select',
  props: {
    model: String,
    formName: String,
    modelURL: String,
    value: {},
    list: {
      type: Array,
      default: () => [],
    },
    excludeList: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'display_name',
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    validationRules: {
      type: String,
      default: 'required-all',
    },
    dataVvAs: String,
    placeholder: {
      type: String,
      default: 'Search',
    },
    tagPlaceholder: {
      type: String,
      default: 'Create new',
    },
    modalSize: {
      type: String,
      default: 'full',
    },
    isTaggable: Boolean,
    inModal: Boolean,
    searchOnMount: Boolean,
    buttonNew: {
      type: Boolean,
      default: true,
    },
    buttonEdit: {
      type: Boolean,
      default: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    searchOnOpen: {
      type: Boolean,
      default: true,
    },
    noWrap: Boolean,
    isLoading: Boolean,
    modelOptions: {}
  },
  components: {
    ModalComponent,
    CreateContact,
    CreateVehicle,
    CreateJobType,
    CreateProduct,
    CreateSupplierAccount,
    CreateReminderList,
  },
  data() {
    return {
      editID: null,
      editURL: path.join(this.model + 's', 'simple-search'),
      modal: {
        active: false,
        title: '',
      },
    }
  },
  watch: {
    model() {
      this.editURL = path.join(this.model + 's', 'simple-search')
      if (this.searchOnMount) {
        this.search('')
          .then((response) => {
            this.$emit('search-completed', response)
          })
      }
    }
  },
  computed:{
    hasSearchListener(){
      return this.$listeners && this.$listeners.search
    },
    hasCreateListener(){
      return this.$listeners && this.$listeners.create
    },
  },
  mounted() {
    if (this.modelURL) {
      this.editURL = this.modelURL
    }
    if (this.searchOnMount) {
      this.search('')
        .then((response) => {
          this.$emit('search-completed', response)
        })
    }
  },
  methods: {
    open(event) {
      this.$emit('open', event)
      if (this.searchOnOpen) {
        this.search('')
      }
    },
    close(event) {
      this.$emit('close', event)
    },
    edit() {
      this.editID = this.value.id
      // show modal
      this.modal.title = 'Edit ' + this.model
      this.modal.active = true
    },
    closeModal(type, obj) {
      let displayName = ''
      if (type === 'contact') {
        displayName = '#' + obj.id + ' ' + obj.first_name + ' ' + obj.last_name + (obj.company_name ? ' (' + obj.company_name + ')' : '')
      } else if (type === 'vehicle') {
        displayName = '#' + obj.id + ' ' + obj.make + ' ' + obj.model
      } else if (type === 'job-type') {
        displayName = obj.name
      } else if (type === 'product') {
        displayName = '#' + obj.id + ' ' + obj.name + ' ' + (obj.code || '')
      } else if (type === 'reminder-list') {
        displayName = '#' + obj.id + ' ' + obj.name
      } else if (type === 'supplier-account') {
        displayName = '#' + obj.id + ' ' + obj.name + ' ' + obj.code
      } else {
        return false
      }

      this.$set(obj, 'display_name', displayName)

      // look if exists and delete if update
      let list = Object.assign([], this.list)
      for (let i in list) {
        if (list[i].id === obj.id) {
          list.splice(i, 1)
        }
      }
      // prepend
      list.unshift(obj)

      this.$emit('update:list', list)
      this.$emit('select', obj)
      this.$emit('input', obj)
      // close modal
      this.modal.active = false
    },
    create(tagName){
      this.$refs.multiselect.deactivate()
      if (this.hasCreateListener) {
        this.$emit('create', tagName)
      } else {
        this.$emit('modal-create-open')
        this.editID = null

        // show modal
        this.modal.title = 'Create ' + this.model
        this.modal.active = true

        this.$nextTick(() => {
          // set data
          if (this.model === 'customer' || this.model === 'supplier') {
            const name = tagName.split(' ')
            this.$refs.contact.set({first_name: name[0], last_name: name[1]})
          } else if (this.model === 'product') {
            this.$refs.product.set({name: tagName})
          } else if (this.model === 'job-type') {
            this.$refs.jobType.set(tagName)
          }
        })
      }
    },
    search(query){
      return new Promise((resolve, reject) => {
        if (this.hasSearchListener) {
          this.$emit('search', query)
          resolve()
        } else {
          this.$emit('update:isLoading', true)
          this.$emit('update:list', [])
          axios.get(this.editURL, {params: {query}})
            .then(response => {
              let list = response.data
              if (this.excludeList.length) {
                list = list.filter(e => this.excludeList.indexOf(e.id) === -1)
              }

              if (this.value && this.value.id) {
                // add this.value in list, if not already in
                if (!list.filter(e => this.value.id === e.id).length) {
                  list.unshift(this.value)
                }
              }

              this.$emit('update:list', list)
              // this.list = response.data
              this.$emit('update:isLoading', false)
              resolve(response)
            })
            .catch(error => {
              this.$emit('update:isLoading', false)
              reject(error)
            })
        }
      })
    },
  }
}
</script>
